import { z } from 'zod'

// ─── Data model ──────────────────────────────────────────────────────────────
// Mirrors the /v3/roadmap contract. Zod rather than plain interfaces because
// these now arrive over the wire and are parsed on the way in.

/** Lifecycle of a roadmap activity. Drives the bar colour (see STATUS_STYLES). */
export const RoadmapActivityStatus = z.enum([
	'todo',
	'in_progress',
	'done',
	'blocked',
	'qa'
])
export type RoadmapActivityStatus = z.infer<typeof RoadmapActivityStatus>

export const ROADMAP_ACTIVITY_STATUS_OPTIONS =
	RoadmapActivityStatus.options satisfies readonly RoadmapActivityStatus[]

/**
 * The gantt row an activity sits on. A fixed set: the backend declares the same
 * four in its contract, so adding one is a contract change on both sides.
 */
export const RoadmapActivityCategory = z.enum([
	'luxe',
	'pro_photo',
	'new_tooling_infra',
	'other'
])
export type RoadmapActivityCategory = z.infer<typeof RoadmapActivityCategory>

/** A per-side pair of numbers: points, a pool, or a percentage. */
export interface SideValues {
	backend: number
	frontend: number
}

/**
 * Dates are plain calendar days (YYYY-MM-DD), no time and no timezone: a bar
 * means "this whole day", so an instant would only invite off-by-one rendering
 * across zones. Both ends are inclusive.
 */
export const RoadmapActivity = z.object({
	category: RoadmapActivityCategory,
	/** The activity's identity: unique, and how it is addressed for edit/delete. */
	title: z.string(),
	dateFrom: z.string(),
	dateTo: z.string(),
	status: RoadmapActivityStatus,
	/** Effort in points, what the form edits. Read straight; the client never
	 *  derives them. */
	backendPoints: z.number(),
	frontendPoints: z.number(),
	/**
	 * Workload each side's effort adds in a week, as a percentage of that side's
	 * point pool, ready to render. The backend does the maths (points × 1.3
	 * buffer ÷ pool), so the chart shows these as-is; the client never computes
	 * them. Zero means that side has no work on it.
	 */
	backendPercentage: z.number(),
	frontendPercentage: z.number(),
	/** Who leads the activity. Free text, shown in the tooltip only; empty if unset. */
	lead: z.string()
})
export type RoadmapActivity = z.infer<typeof RoadmapActivity>

/**
 * A gantt row. Built on the client by grouping the flat activity list the API
 * returns: the API has no concept of a row, only of activities that name one.
 */
export interface RoadmapCategory {
	id: RoadmapActivityCategory
	title: string
	activities: RoadmapActivity[]
}

// ─── Request payloads ────────────────────────────────────────────────────────

/** What the current user may do with the roadmap. */
export const RoadmapPermissions = z.object({
	canEdit: z.boolean()
})
export type RoadmapPermissions = z.infer<typeof RoadmapPermissions>

export interface HttpCreateRoadmapActivityPayload {
	category: RoadmapActivityCategory
	title: string
	dateFrom: string
	dateTo: string
	status: RoadmapActivityStatus
	backendPoints: number
	frontendPoints: number
	lead: string
}

/**
 * The fields an edit may change; a new `title` renames the activity. Every
 * field is optional except `lead`, which every update must carry.
 */
export type RoadmapActivityChanges = Partial<
	Omit<HttpCreateRoadmapActivityPayload, 'lead'>
> & { lead: string }

/** An update locates the activity by its current title, then applies `changes`. */
export interface HttpUpdateRoadmapActivityPayload {
	title: string
	changes: RoadmapActivityChanges
}

/**
 * A vertical band spanning every row: team holidays, code freezes, milestones.
 * Purely informative, rendered behind the bars.
 */
export interface RoadmapOverlay {
	id: string
	title: string
	dateFrom: string
	dateTo: string
}

export interface RoadmapConfig {
	/** Timeline bounds. Activities outside this window are not drawn. */
	dateFrom: string
	dateTo: string
	categories: RoadmapCategory[]
	overlays: RoadmapOverlay[]
}

// ─── Derived view models ─────────────────────────────────────────────────────

/**
 * The chart's x axis: working days in order, weekends off the axis entirely, so
 * every horizontal measure counts *units* (an index into `days`) rather than
 * calendar days. Built by buildTimeline.
 *
 * The axis runs over whole weeks, so it can reach past the roadmap window at
 * either end: a year starting on a Thursday pulls in the Monday to Wednesday
 * before it. Those padding days are on the axis only to keep the first and last
 * week whole; nothing is scheduled on them.
 */
export interface Timeline {
	days: string[]
	indexByDate: Map<string, number>
	totalUnits: number
	/**
	 * The window itself, as unit indices. Everything outside is padding, and the
	 * range is contiguous, so two bounds describe it completely.
	 */
	firstEnabledIndex: number
	lastEnabledIndex: number
}

/** An activity resolved to working-day offsets from the timeline start. */
export interface PositionedActivity {
	activity: RoadmapActivity
	/** Working days between the timeline start and the activity start. */
	offsetUnits: number
	/** Inclusive length in working days, so it is always >= 1. */
	spanUnits: number
}

/** A single stacking row inside a category, see packIntoLanes. */
export type ActivityLane = PositionedActivity[]

/** One header column: a single week, five working days wide. */
export interface TimelineColumn {
	key: string
	label: string
	offsetUnits: number
	spanUnits: number
}

/** A month band above the columns. */
export interface TimelineMonth {
	key: string
	label: string
	/** Unabbreviated name plus year, which the label alone never carries. */
	tooltip: string
	/** Lies wholly outside the window: a month the week padding dragged in. */
	isPadding: boolean
	offsetUnits: number
	spanUnits: number
}
